import { CancelOutlined, CheckCircleOutline, Close } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from "@mui/material";
import moment from "moment-timezone";
import React from "react";
import { t } from "ttag";
import { IDeviceHealthRecord, IUnitRef } from "../../models/DeviceHealth.types";
import useStyles from "./DeviceRecordPopup.style";
import {
  getProblemGroups,
  getProblemUnits,
  hasHealthIssues,
  isDeviceConnected
} from "./deviceHealth.utils";

interface IDeviceRecordPopupProps {
  record: IDeviceHealthRecord;
  /** territory label resolved from the filter options (the record carries the enum value) */
  territoryName: string;
  onClose: () => void;
}

const formatTimestamp = (timestamp?: number | null) => {
  if (!timestamp) {
    return "-";
  }

  return moment(timestamp).format("llll");
};

const DeviceRecordPopup: React.FC<IDeviceRecordPopupProps> = ({ record, territoryName, onClose }) => {
  const classes: any = useStyles();

  const unhealthy = hasHealthIssues(record);
  const problemGroups = getProblemGroups().filter(({ key }) => getProblemUnits(record, key).length > 0);

  const renderOverviewField = (label: string, value: string, isProblem = false) => (
    <div className={classes.overviewField} key={label}>
      <Typography className={classes.fieldLabel}>{label}</Typography>
      <Typography className={isProblem ? classes.fieldValueProblem : classes.fieldValue}>
        {value}
      </Typography>
    </div>
  );

  const renderConnectivity = () => {
    if (isDeviceConnected(record)) {
      return renderOverviewField(t`Device connectivity status`, t`Connected`);
    }

    return (
      <>
        {renderOverviewField(t`Device connectivity status`, t`Disconnected`, true)}
        {renderOverviewField(
          t`Disconnected since`,
          formatTimestamp(record.deviceConnectivity?.disconnectedTimestamp),
          true
        )}
      </>
    );
  };

  const renderCountTile = (label: string, count: number) => (
    <div className={classes.countTile} key={label}>
      <Typography className={classes.countValue}>{count}</Typography>
      <Typography className={classes.countLabel}>{label}</Typography>
    </div>
  );

  const renderUnitChip = (unit: IUnitRef, withTimestamp?: boolean) => (
    <div className={classes.unitChip} key={unit.unitId}>
      <Typography className={classes.unitName}>{unit.name || unit.unitId}</Typography>
      {withTimestamp && !!unit.disconnectedTimestamp && (
        <Typography className={classes.unitTimestamp}>
          {formatTimestamp(unit.disconnectedTimestamp)}
        </Typography>
      )}
    </div>
  );

  const renderProblems = () => {
    if (!problemGroups.length) {
      return (
        <div className={classes.noProblems}>
          <CheckCircleOutline className={classes.noProblemsIcon} />
          <Typography className={classes.noProblemsText}>
            {t`All units are associated, assigned to a system and connected.`}
          </Typography>
        </div>
      );
    }

    return problemGroups.map(({ key, label, withTimestamp }) => {
      const units = getProblemUnits(record, key);

      return (
        <div className={classes.problemGroup} key={key}>
          <div className={classes.problemHeader}>
            <Typography className={classes.problemLabel}>{label}</Typography>
            <Typography className={classes.problemCount}>{units.length}</Typography>
          </div>
          <div className={classes.unitList}>
            {units.map((unit) => renderUnitChip(unit, withTimestamp))}
          </div>
        </div>
      );
    });
  };

  const renderHealthBadge = () => {
    if (unhealthy) {
      return (
        <div className={classes.badgeUnhealthy}>
          <CancelOutlined className={classes.badgeIcon} />
          <Typography className={classes.badgeText}>{t`Issues detected`}</Typography>
        </div>
      );
    }

    return (
      <div className={classes.badgeHealthy}>
        <CheckCircleOutline className={classes.badgeIcon} />
        <Typography className={classes.badgeText}>{t`Healthy`}</Typography>
      </div>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.titleLeft}>
          <Typography className={classes.titleText}>{t`Device Health Record`}</Typography>
        </div>
        <div className={classes.titleRight}>
          {renderHealthBadge()}
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.sectionTitle}>{t`Overview`}</Typography>
        <div className={classes.overviewGrid}>
          {renderOverviewField(t`Customer`, record.customer?.name || "-")}
          {renderOverviewField(t`Customer sales territory`, territoryName)}
          {renderOverviewField(t`Site`, record.site?.name || "-")}
          {renderOverviewField(t`Device serial`, record.serial)}
          {renderConnectivity()}
        </div>

        <Divider className={classes.divider} />

        <Typography className={classes.sectionTitle}>{t`Units`}</Typography>
        <div className={classes.countRow}>
          {renderCountTile(t`Number of Control units`, record.controlUnitCount || 0)}
          {renderCountTile(t`Number of Service units`, record.serviceUnitCount || 0)}
          {renderCountTile(t`Number of Outdoor units`, record.outdoorUnitCount || 0)}
        </div>

        <Divider className={classes.divider} />

        <Typography className={classes.sectionTitle}>{t`Detected problems`}</Typography>
        {renderProblems()}
      </DialogContent>
    </Dialog>
  );
};

export default DeviceRecordPopup;
